// Verträge des Ausgangs (SPEZIFIKATION §4.1, §8, §9, §10.2): Vorprüfung, Abruf, Liste, Download, Übermittlung,
// nachträgliche Ausgabe und die Vormerkung „Storno ohne XML“ im Ablauf „Rechnung berichtigen“.
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { zListPage } from '@shared/list'
import { z } from 'zod'
import { OUTBOUND_READ, OUTBOUND_WRITE, zCheck, zDay, zDelivery, zOutbound, zPreflightResult } from './common'

const zReason = z
  .string({ error: 'Bitte geben Sie einen Grund an.' })
  .trim()
  .min(3, { error: 'Bitte geben Sie einen Grund an (mindestens 3 Zeichen).' })
  .max(500, { error: 'Der Grund ist zu lang (höchstens 500 Zeichen).' })

export const outboundPreflight = defineContract({
  name: 'einvoice.outbound.preflight',
  title: 'E-Rechnung vorab prüfen',
  description:
    'Prüft für eine Rechnung oder Rechnungskorrektur – auch einen Entwurf –, ob beim Festschreiben eine E-Rechnung entsteht und ob alle Angaben dafür vorliegen.',
  llm: {
    description:
      'Prüft einen Beleg (Rechnung, Rechnungskorrektur, auch Entwurf) vor dem Festschreiben: Muss eine XRechnung entstehen (gesetzliche Pflicht, Wunsch des Kunden, Leitweg-ID) und fehlt dafür etwas (Befunde P-01 … P-20 mit Feld und Korrekturweg)? Ändert nichts.',
    keywords: ['E-Rechnung', 'XRechnung', 'Vorprüfung', 'Leitweg-ID', 'fehlt'],
  },
  input: z.strictObject({ documentId: zId }),
  output: zPreflightResult,
  permission: OUTBOUND_READ,
  risk: 'read',
  scope: 'record',
})

const zDraftLine = z.strictObject({
  kind: z.enum(['item', 'text', 'fee', 'discount', 'deposit', 'deposit_return', 'rounding']),
  taxCodeId: zId.nullish(),
  /** UN/ECE-Code der Einheit (Abzug einer Rücknahme: Code der Rücknahmezeile) */
  unitCode: z.string().max(3).nullish(),
  qty: z.int(),
  netCents: z.int(),
  grossCents: z.int(),
})

export const outboundPreflightDraft = defineContract({
  name: 'einvoice.outbound.preflightDraft',
  title: 'E-Rechnung einer geplanten Rechnung prüfen',
  description:
    'Prüft eine geplante Rechnung ohne Beleg (Vorschau des Rechnungslaufs): Muss eine E-Rechnung entstehen und liegen alle Angaben vor?',
  llm: { description: 'Nur für die Vorschau des Rechnungslaufs (Finanzen).', keywords: [] },
  input: z.strictObject({
    documentType: z.enum(['invoice', 'credit_note']),
    partnerId: zId,
    locationId: zId,
    documentDate: zDay,
    serviceDate: zDay.nullish(),
    serviceDateTo: zDay.nullish(),
    priceMode: z.enum(['net', 'gross']),
    /** Lieferscheine bzw. Rücknahmen, aus denen die Rechnung entstünde */
    sourceDocumentIds: z.array(zId).max(100).default([]),
    externalRef: z.string().max(120).nullish(),
    buyerReference: z.string().max(120).nullish(),
    lines: z.array(zDraftLine).max(1000),
    netCents: z.int(),
    taxCents: z.int(),
    grossCents: z.int(),
  }),
  output: zPreflightResult,
  permission: OUTBOUND_READ,
  risk: 'read',
  scope: (input) => [input.locationId],
  assistant: 'never',
})

export const outboundGet = defineContract({
  name: 'einvoice.outbound.get',
  title: 'E-Rechnung eines Belegs anzeigen',
  description: 'Die abgelegte XRechnung eines Belegs mit Prüfergebnis und Übermittlungen – oder keine.',
  llm: {
    description:
      'Liefert zur Rechnung (documentId) die abgelegte E-Rechnung: Datei, Syntax, Typcode, Hash, Prüfergebnis und wann sie heruntergeladen oder als übermittelt markiert wurde. `outbound: null` = keine E-Rechnung.',
    keywords: ['E-Rechnung', 'XRechnung', 'XML', 'übermittelt'],
  },
  input: z.strictObject({ documentId: zId }),
  output: z.object({
    outbound: zOutbound.nullable(),
    check: zCheck.nullable(),
    deliveries: z.array(zDelivery),
  }),
  permission: OUTBOUND_READ,
  risk: 'read',
  scope: 'record',
})

export const zOutboundRow = zOutbound.extend({
  partnerName: z.string().nullable(),
  /**
   * zuletzt heruntergeladen, als übermittelt markiert oder per E-Mail versandt; `status` failed = die E-Mail wurde
   * endgültig nicht zugestellt (zählt nicht als übermittelt, docs/anmeldung/SPEZIFIKATION.md §5.1)
   */
  lastDelivery: z
    .object({
      channel: z.string(),
      channelLabel: z.string(),
      at: z.date(),
      status: z.enum(['queued', 'done', 'failed']).optional(),
    })
    .nullable(),
})

export const outboundList = defineContract({
  name: 'einvoice.outbound.list',
  title: 'Ausgestellte E-Rechnungen',
  description: 'Alle ausgestellten E-Rechnungen der eigenen Standorte, neueste zuerst.',
  llm: {
    description:
      'Listet ausgestellte E-Rechnungen (XRechnung) der Standorte, an denen der Nutzer Rechnungen lesen darf – neueste zuerst; Filter: Zeitraum (Ausstellungsdatum), Kunde, nur noch nicht übermittelte.',
    keywords: ['E-Rechnungen', 'XRechnung', 'Ausgang', 'nicht übermittelt'],
  },
  input: z.strictObject({
    from: zDay.optional(),
    to: zDay.optional(),
    partnerId: zId.optional(),
    /** nur E-Rechnungen ohne erledigte Übermittlung (Download, von Hand, Portal, per E-Mail angenommen) – gescheiterte zählen nicht */
    undelivered: z.boolean().default(false),
    page: zListPage,
  }),
  output: z.object({ rows: z.array(zOutboundRow), nextCursor: z.string().nullable() }),
  permission: OUTBOUND_READ,
  risk: 'read',
  scope: 'any',
})

export const outboundDownload = defineContract({
  name: 'einvoice.outbound.download',
  title: 'E-Rechnung herunterladen',
  description: 'Liefert die abgelegte XML-Datei zum Herunterladen und hält den Download im Protokoll fest.',
  llm: { description: 'Nur für die Oberfläche: Download der abgelegten XML-Datei (protokolliert).', keywords: [] },
  input: z.strictObject({ outboundId: zId }),
  output: z.object({ fileId: zId, url: z.string(), fileName: z.string(), deliveryId: zId }),
  permission: OUTBOUND_READ,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const outboundMarkSent = defineContract({
  name: 'einvoice.outbound.markSent',
  title: 'E-Rechnung als übermittelt markieren',
  description:
    'Hält fest, dass die E-Rechnung dem Kunden übermittelt wurde – von Hand oder über das Portal einer Behörde.',
  llm: {
    description:
      'Markiert eine ausgestellte E-Rechnung als übermittelt: `channel` manual = von Hand (z. B. per E-Mail aus dem eigenen Postfach) · portal = im Portal des öffentlichen Auftraggebers hochgeladen. Mit kurzer Notiz.',
    keywords: ['übermittelt', 'versandt', 'Portal hochgeladen', 'E-Rechnung verschickt'],
  },
  input: z.strictObject({
    outboundId: zId,
    channel: z.enum(['manual', 'portal'], { error: 'Bitte wählen Sie, wie die E-Rechnung übermittelt wurde.' }),
    recipient: z.string().trim().max(320, { error: 'Der Empfänger ist zu lang (höchstens 320 Zeichen).' }).nullish(),
    note: z.string().trim().max(500, { error: 'Die Notiz ist zu lang (höchstens 500 Zeichen).' }).nullish(),
  }),
  output: z.object({ deliveryId: zId }),
  permission: OUTBOUND_WRITE,
  risk: 'write',
  scope: 'record',
  assistant: 'opt-in',
})

export const outboundIssue = defineContract({
  name: 'einvoice.outbound.issue',
  title: 'E-Rechnung nachträglich ausstellen',
  description:
    'Stellt für eine festgeschriebene Rechnung oder Rechnungskorrektur ohne E-Rechnung einmalig eine XRechnung aus – etwa für Belege vor dem Einschalten der Funktion.',
  llm: {
    description:
      'Stellt für einen festgeschriebenen Beleg ohne XML einmal eine XRechnung aus (nur Beleg und Schnappschüsse, nie der heutige Kundenstamm – fehlende E-Rechnungsfelder älterer Belege kommen aus dem Stamm und stehen als Hinweis im Prüfergebnis). Mit Grund. Geht nur einmal je Beleg.',
    keywords: ['E-Rechnung nachträglich', 'XRechnung erzeugen', 'E-Rechnung ausstellen'],
  },
  input: z.strictObject({ documentId: zId, reason: zReason }),
  /** in der Vorschau null: Die Datei wird geprüft und gebaut, aber nicht abgelegt */
  output: z.object({
    outboundId: zId.nullable(),
    fileId: zId.nullable(),
    fileName: z.string().nullable(),
    typeCode: z.string().nullable(),
    syntax: z.string().nullable(),
  }),
  permission: 'einvoice.outbound.issue',
  risk: 'write',
  scope: 'record',
  assistant: 'opt-in',
})

export const outboundSuppressReversal = defineContract({
  name: 'einvoice.outbound.suppressReversal',
  title: 'Storno ohne E-Rechnung vormerken',
  description:
    'Merkt vor, dass das Storno dieser Rechnung keine eigene E-Rechnung bekommt – nur im Ablauf „Rechnung berichtigen“ (der Entwurf der berichtigten Rechnung muss bestehen), unmittelbar vor dem Storno. Die Vormerkung gilt nur, solange es die berichtigte Rechnung gibt.',
  llm: { description: 'Nur für den Ablauf „Rechnung berichtigen“ der Finanzen.', keywords: [] },
  input: z.strictObject({ documentId: zId, reason: zReason }),
  output: z.object({ id: zId }),
  permission: OUTBOUND_WRITE,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

// ---- Ereignisse (§10.3) -----------------------------------------------------------------------------------------------

export const outboundIssued = defineEvent({
  name: 'einvoice.outbound.issued',
  title: 'E-Rechnung ausgestellt',
  payload: z.object({
    documentId: zId,
    outboundId: zId,
    number: z.string(),
    typeCode: z.string(),
    syntax: z.string(),
    partnerId: zId.nullable(),
  }),
})

export const inboundReceived = defineEvent({
  name: 'einvoice.inbound.received',
  title: 'E-Rechnung eingegangen',
  payload: z.object({ inboundId: zId, source: z.enum(['upload', 'email', 'peppol']) }),
})

export const inboundParsed = defineEvent({
  name: 'einvoice.inbound.parsed',
  title: 'Eingegangene E-Rechnung gelesen',
  payload: z.object({
    inboundId: zId,
    outcome: z.enum(['ok', 'warnings', 'errors', 'not_einvoice', 'unreadable', 'unsupported', 'rejected']),
    supplierId: zId.nullable(),
  }),
})

export const inboundCaptured = defineEvent({
  name: 'einvoice.inbound.captured',
  title: 'Eingegangene E-Rechnung erfasst',
  payload: z.object({ inboundId: zId, documentId: zId, supplierId: zId }),
})
